import {
  PlayerInput,
  EMPTY_INPUT,
  KartState,
  ItemBoxState,
  ProjectileState,
  BananaState,
  CoinState,
  RaceResult,
  ItemType,
  MapId,
  TICK_MS,
  TOTAL_LAPS,
  MAX_SPEED,
  KART_RADIUS,
  BOUNCE,
  BOOST_DURATION,
  PROJECTILE_RADIUS,
  STUN_DURATION,
  ITEM_RESPAWN,
  ITEM_BOX_RADIUS,
  TrackPoint,
} from '../../shared/types.js';
import { config } from '../../shared/config.js';
import {
  initTrack,
  findClosestPoint,
  checkWallCollision,
  updateCheckpoint,
  type InitializedTrack,
} from '../../shared/track.js';
import { getMapDef } from '../../shared/maps.js';
import { simulateKart, defaultNewKartFields, type PhysicsTrackContext } from '../../shared/physics.js';
import { initHazards, updateHazards, checkHazardCollision, type HazardState } from '../../shared/hazards.js';
import { BotAI } from './BotAI.js';

// ============================================================
// Internal types
// ============================================================

interface ServerKart {
  id: string;
  name: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  yaw: number;
  speed: number;
  steering: number;
  lap: number;
  checkpoint: number;
  checkpointBits: number;
  progress: number;
  position: number;
  item: ItemType | null;
  stunTimer: number;
  boostTimer: number;
  finished: boolean;
  finishTime: number;
  color: number;
  lastInputSeq: number;
  lastInput: PlayerInput;
  pendingInputs: Array<{ seq: number; input: PlayerInput }>;
  // New Mario Kart fields
  driftTime: number;
  coins: number;
  slipstreamTimer: number;
  starTimer: number;
  shrinkTimer: number;
  blooperTimer: number;
  wrongWay: boolean;
  offRoad: boolean;
  heldItemBehind: ItemType | null;
  orbitItems: number;
  rouletteTimer: number;
  trickBoosting: boolean;
  // Item roulette: the actual item decided server-side
  rouletteResult: ItemType | null;
  // Held item behind tracking: was item button held last tick
  prevItemHeld: boolean;
  // Starting line boost tracking
  hasReceivedFirstInput: boolean;
  firstInputTime: number;
}

interface ServerProjectile {
  id: string;
  ownerId: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vz: number;
  shellType: 'green' | 'red' | 'blue' | 'legacy';
  bounceCount: number;
  spawnX: number;
  spawnZ: number;
  // Red shell: target kart id
  targetId: string | null;
  // Blue shell: phase tracking
  bluePhase: 'fly' | 'dive';
  blueTargetX: number;
  blueTargetZ: number;
}

interface ServerBanana {
  id: string;
  ownerId: string;
  x: number;
  z: number;
  spawnTime: number; // tick when spawned, for owner immunity
}

interface ServerCoin {
  index: number;
  x: number;
  z: number;
  active: boolean;
  respawnTimer: number;
}

interface ServerItemBox {
  index: number;
  x: number;
  z: number;
  active: boolean;
  respawnTimer: number;
}

// ============================================================
// Snapshot type (what we broadcast each tick)
// ============================================================

export interface Snapshot {
  type: 'snapshot';
  tick: number;
  time: number;
  karts: KartState[];
  items: ItemBoxState[];
  projectiles: ProjectileState[];
  bananas: BananaState[];
  coins: CoinState[];
  lastSeq: Record<string, number>;
}

// ============================================================
// Events emitted by Simulation
// ============================================================

export interface SimulationEvents {
  onItemPickup: (playerId: string, item: ItemType, boxIndex: number) => void;
  onPlayerHit: (playerId: string, byPlayerId: string) => void;
  onLapComplete: (playerId: string, lap: number) => void;
  onRaceFinish: (rankings: RaceResult[]) => void;
  onLightningStrike?: (byPlayerId: string) => void;
  onBlooperInk?: (byPlayerId: string) => void;
}

// ============================================================
// Item distribution tables (B1)
// ============================================================

interface WeightedItem {
  item: ItemType;
  weight: number;
}

const ITEMS_1ST: WeightedItem[] = [
  { item: ItemType.Banana, weight: 35 },
  { item: ItemType.GreenShell, weight: 35 },
  { item: ItemType.SpeedBoost, weight: 20 },
  { item: ItemType.Blooper, weight: 10 },
];

const ITEMS_MID: WeightedItem[] = [
  { item: ItemType.RedShell, weight: 25 },
  { item: ItemType.GreenShell, weight: 20 },
  { item: ItemType.TripleGreenShell, weight: 15 },
  { item: ItemType.SpeedBoost, weight: 15 },
  { item: ItemType.Star, weight: 10 },
  { item: ItemType.Banana, weight: 15 },
];

const ITEMS_LAST: WeightedItem[] = [
  { item: ItemType.BlueShell, weight: 10 },
  { item: ItemType.Lightning, weight: 10 },
  { item: ItemType.Star, weight: 20 },
  { item: ItemType.TripleRedShell, weight: 15 },
  { item: ItemType.SpeedBoost, weight: 25 },
  { item: ItemType.Blooper, weight: 20 },
];

function pickWeighted(table: WeightedItem[]): ItemType {
  const totalWeight = table.reduce((sum, w) => sum + w.weight, 0);
  let roll = Math.random() * totalWeight;
  for (const entry of table) {
    roll -= entry.weight;
    if (roll <= 0) return entry.item;
  }
  return table[table.length - 1].item;
}

function pickItemByPosition(position: number, totalKarts: number): ItemType {
  if (totalKarts <= 1) return pickWeighted(ITEMS_MID);
  const ratio = (position - 1) / (totalKarts - 1); // 0=1st, 1=last
  if (ratio < 0.25) return pickWeighted(ITEMS_1ST);
  if (ratio < 0.7) return pickWeighted(ITEMS_MID);
  return pickWeighted(ITEMS_LAST);
}

// ============================================================
// Helper: is item holdable behind kart
// ============================================================

function isHoldableItem(item: ItemType): boolean {
  return item === ItemType.Banana;
}

// ============================================================
// Simulation
// ============================================================

export class Simulation {
  private track: InitializedTrack;
  private roadWidth: number;
  private physicsTrack: PhysicsTrackContext;
  private karts: Map<string, ServerKart> = new Map();
  kartList: ServerKart[] = []; // ordered list for iteration (public for Room diagnostics)
  private itemBoxes: ServerItemBox[] = [];
  private projectiles: ServerProjectile[] = [];
  private bananas: ServerBanana[] = [];
  private coins: ServerCoin[] = [];
  private hazards: HazardState[] = [];
  private currentTick = 0;
  private raceStartTime = 0;
  private countdownEndTime = 0; // when GO happens
  private nextProjectileId = 0;
  private nextBananaId = 0;
  private events: SimulationEvents;
  private raceComplete = false;
  private botIds: Set<string>;
  private botAI: BotAI;

  constructor(
    players: Array<{ id: string; name: string; color: number; gridIndex: number }>,
    events: SimulationEvents,
    mapId?: MapId,
    botIdList?: string[]
  ) {
    this.events = events;
    this.botIds = new Set(botIdList || []);
    this.botAI = new BotAI();

    // Initialize track from map definition if provided, otherwise use defaults
    if (mapId) {
      const mapDef = getMapDef(mapId);
      this.track = initTrack(mapDef.controlPoints, mapDef.startPositions, mapDef.roadWidth, mapDef.rampPositions);
      this.roadWidth = mapDef.roadWidth;
    } else {
      this.track = initTrack();
      this.roadWidth = this.track.roadWidth;
    }

    // Build physics track context for shared physics
    this.physicsTrack = {
      path: this.track.path,
      roadWidth: this.roadWidth,
      ramps: this.track.ramps,
    };

    // Initialize bot AI states with rank-based skill (0 = best)
    const botArray = Array.from(this.botIds);
    for (let i = 0; i < botArray.length; i++) {
      this.botAI.initBot(botArray[i], i, botArray.length);
    }

    // Get defaults for new kart fields
    const defaults = defaultNewKartFields();

    // Create karts at start positions
    for (const p of players) {
      const startPos = this.track.startPositions[p.gridIndex];
      const kart: ServerKart = {
        id: p.id,
        name: p.name,
        x: startPos.x,
        y: 0,
        z: startPos.z,
        vx: 0,
        vy: 0,
        vz: 0,
        yaw: startPos.yaw,
        speed: 0,
        steering: 0,
        lap: 0,
        checkpoint: 0,
        checkpointBits: 0,
        progress: 0,
        position: p.gridIndex + 1,
        item: null,
        stunTimer: 0,
        boostTimer: 0,
        finished: false,
        finishTime: 0,
        color: p.color,
        lastInputSeq: -1,
        lastInput: { ...EMPTY_INPUT },
        pendingInputs: [],
        // New fields from defaults
        driftTime: defaults.driftTime ?? 0,
        coins: defaults.coins ?? 0,
        slipstreamTimer: defaults.slipstreamTimer ?? 0,
        starTimer: defaults.starTimer ?? 0,
        shrinkTimer: defaults.shrinkTimer ?? 0,
        blooperTimer: defaults.blooperTimer ?? 0,
        wrongWay: defaults.wrongWay ?? false,
        offRoad: defaults.offRoad ?? false,
        heldItemBehind: defaults.heldItemBehind ?? null,
        orbitItems: defaults.orbitItems ?? 0,
        rouletteTimer: defaults.rouletteTimer ?? 0,
        trickBoosting: defaults.trickBoosting ?? false,
        // Internal tracking
        rouletteResult: null,
        prevItemHeld: false,
        hasReceivedFirstInput: false,
        firstInputTime: 0,
      };
      this.karts.set(p.id, kart);
      this.kartList.push(kart);
    }

    // Create item boxes
    for (let i = 0; i < this.track.itemPositions.length; i++) {
      const pos = this.track.itemPositions[i];
      this.itemBoxes.push({
        index: i,
        x: pos.x,
        z: pos.z,
        active: true,
        respawnTimer: 0,
      });
    }

    // Create coins from track positions (A6)
    for (let i = 0; i < this.track.coinPositions.length; i++) {
      const pos = this.track.coinPositions[i];
      this.coins.push({
        index: i,
        x: pos.x,
        z: pos.z,
        active: true,
        respawnTimer: 0,
      });
    }

    // Initialize hazards if map has them
    if (mapId) {
      const mapDef = getMapDef(mapId);
      if ((mapDef as any).hazardDefs) {
        this.hazards = initHazards(
          (mapDef as any).hazardDefs,
          this.track.path,
          this.roadWidth
        );
      }
    }

    this.raceStartTime = Date.now();
    this.countdownEndTime = this.raceStartTime; // GO is now
  }

  // --------------------------------------------------------
  // Input handling
  // --------------------------------------------------------

  receiveInput(playerId: string, seq: number, input: PlayerInput): void {
    const kart = this.karts.get(playerId);
    if (!kart) return;

    // Validate input -- all fields must be booleans
    const sanitized: PlayerInput = {
      forward: input.forward === true,
      backward: input.backward === true,
      left: input.left === true,
      right: input.right === true,
      drift: input.drift === true,
      item: input.item === true,
      trick: input.trick === true,
    };

    kart.pendingInputs.push({ seq, input: sanitized });

    // Clamp pending inputs to prevent memory growth
    if (kart.pendingInputs.length > 10) {
      kart.pendingInputs.splice(0, kart.pendingInputs.length - 10);
    }

    // Starting line boost detection (A2)
    if (!kart.hasReceivedFirstInput && sanitized.forward) {
      kart.hasReceivedFirstInput = true;
      kart.firstInputTime = Date.now();
    }
  }

  // --------------------------------------------------------
  // Main tick
  // --------------------------------------------------------

  tick(): { snapshot: Snapshot; raceComplete: boolean; rankings: RaceResult[] | null } {
    this.currentTick++;
    const dt = TICK_MS / 1000;

    // 0. Generate bot inputs
    for (const kart of this.kartList) {
      if (!this.botIds.has(kart.id)) continue;
      if (kart.finished) continue;

      const botInput = this.botAI.generateInput(
        kart.id,
        kart,
        this.track.path,
        this.track.numPoints,
        dt,
        this.kartList,
        this.bananas,
        this.coins.filter(c => c.active),
      );
      kart.lastInput = botInput;
    }

    // 1. Process human inputs
    for (const kart of this.kartList) {
      if (this.botIds.has(kart.id)) continue; // bots already handled above
      if (kart.pendingInputs.length > 0) {
        const latest = kart.pendingInputs[kart.pendingInputs.length - 1];
        kart.lastInput = latest.input;
        kart.lastInputSeq = latest.seq;
        kart.pendingInputs.length = 0;
      }
    }

    // 2. Starting line boost check (A2)
    this.checkStartingBoosts();

    // 3. Update kart physics using shared simulateKart
    const allKartStates = this.kartList.map(k => this.toKartState(k));
    for (const kart of this.kartList) {
      if (kart.finished) continue;
      const kartState = this.toKartState(kart);
      const newState = simulateKart(kartState, kart.lastInput, dt, this.physicsTrack, allKartStates);
      this.copyPhysicsBack(kart, newState);
    }

    // 4. Kart-kart collisions (including star knockback)
    this.handleKartCollisions();

    // 5. Item box collection
    this.handleItemBoxCollection();

    // 6. Coin collection (A6)
    this.handleCoinCollection();

    // 7. Item use / held item logic
    for (const kart of this.kartList) {
      if (kart.finished || kart.stunTimer > 0) continue;
      this.handleItemLogic(kart);
    }

    // 8. Projectile updates
    this.updateProjectiles(dt);

    // 9. Banana collision
    this.handleBananaCollisions();

    // 10. Orbit item shield (absorb incoming projectiles)
    this.handleOrbitShield();

    // 11. Item box respawn
    for (const box of this.itemBoxes) {
      if (!box.active) {
        box.respawnTimer -= dt;
        if (box.respawnTimer <= 0) {
          box.active = true;
          box.respawnTimer = 0;
        }
      }
    }

    // 12. Coin respawn
    for (const coin of this.coins) {
      if (!coin.active) {
        coin.respawnTimer -= dt;
        if (coin.respawnTimer <= 0) {
          coin.active = true;
          coin.respawnTimer = 0;
        }
      }
    }

    // 13. Hazard updates
    if (this.hazards.length > 0) {
      updateHazards(this.hazards, dt, this.track.path, this.roadWidth);
      this.handleHazardCollisions();
    }

    // 14. Checkpoints & laps
    this.updateCheckpointsAndLaps();

    // 15. Race positions
    this.updateRacePositions();

    // 16. Track prev item held state
    for (const kart of this.kartList) {
      kart.prevItemHeld = kart.lastInput.item;
    }

    // 17. Check race complete
    let rankings: RaceResult[] | null = null;
    if (!this.raceComplete) {
      const allFinished = this.kartList.every((k) => k.finished);
      if (allFinished && this.kartList.length > 0) {
        this.raceComplete = true;
        rankings = this.buildRankings();
        this.events.onRaceFinish(rankings);
      }
    }

    // 18. Build snapshot
    const snapshot = this.buildSnapshot();

    return { snapshot, raceComplete: this.raceComplete, rankings };
  }

  // --------------------------------------------------------
  // Convert ServerKart <-> KartState
  // --------------------------------------------------------

  private toKartState(kart: ServerKart): KartState {
    return {
      id: kart.id,
      name: kart.name,
      x: kart.x,
      y: kart.y,
      z: kart.z,
      vx: kart.vx,
      vy: kart.vy,
      vz: kart.vz,
      yaw: kart.yaw,
      speed: kart.speed,
      steering: kart.steering,
      lap: kart.lap,
      checkpoint: kart.checkpoint,
      progress: kart.progress,
      position: kart.position,
      item: kart.item,
      stunTimer: kart.stunTimer,
      boostTimer: kart.boostTimer,
      finished: kart.finished,
      finishTime: kart.finishTime,
      color: kart.color,
      driftTime: kart.driftTime,
      coins: kart.coins,
      slipstreamTimer: kart.slipstreamTimer,
      starTimer: kart.starTimer,
      shrinkTimer: kart.shrinkTimer,
      blooperTimer: kart.blooperTimer,
      wrongWay: kart.wrongWay,
      offRoad: kart.offRoad,
      heldItemBehind: kart.heldItemBehind,
      orbitItems: kart.orbitItems,
      rouletteTimer: kart.rouletteTimer,
      trickBoosting: kart.trickBoosting,
    };
  }

  private copyPhysicsBack(kart: ServerKart, state: KartState): void {
    kart.x = state.x;
    kart.y = state.y;
    kart.z = state.z;
    kart.vx = state.vx;
    kart.vy = state.vy;
    kart.vz = state.vz;
    kart.yaw = state.yaw;
    kart.speed = state.speed;
    kart.steering = state.steering;
    kart.boostTimer = state.boostTimer;
    kart.stunTimer = state.stunTimer;
    kart.driftTime = state.driftTime;
    kart.coins = state.coins;
    kart.slipstreamTimer = state.slipstreamTimer;
    kart.starTimer = state.starTimer;
    kart.shrinkTimer = state.shrinkTimer;
    kart.blooperTimer = state.blooperTimer;
    kart.wrongWay = state.wrongWay;
    kart.offRoad = state.offRoad;
    kart.trickBoosting = state.trickBoosting;
    kart.rouletteTimer = state.rouletteTimer;
  }

  // --------------------------------------------------------
  // Starting line boost (A2)
  // --------------------------------------------------------

  private checkStartingBoosts(): void {
    // Only relevant on very early ticks
    if (this.currentTick > 300) return; // ~10 seconds at 30 tps

    const now = Date.now();
    const goTime = this.countdownEndTime;
    const windowStart = goTime - config.specialItems.startBoostWindow * 1000;
    const earlyThreshold = goTime - config.specialItems.startBoostWindow * 2 * 1000;

    for (const kart of this.kartList) {
      if (!kart.hasReceivedFirstInput) continue;
      if (kart.boostTimer > 0 || kart.stunTimer > 0) continue;
      if (kart.firstInputTime === 0) continue;

      const inputTime = kart.firstInputTime;
      // Reset so we don't re-process
      kart.firstInputTime = 0;

      if (inputTime >= windowStart && inputTime <= goTime) {
        // Perfect boost window
        kart.boostTimer = config.specialItems.startBoostDuration;
        if (kart.speed < MAX_SPEED) kart.speed = MAX_SPEED;
      } else if (inputTime < windowStart && inputTime >= earlyThreshold) {
        // Too early: stall
        kart.stunTimer = config.specialItems.startBoostEarly;
      }
    }
  }

  // --------------------------------------------------------
  // Kart-kart collisions
  // --------------------------------------------------------

  private handleKartCollisions(): void {
    for (let i = 0; i < this.kartList.length; i++) {
      const kA = this.kartList[i];
      for (let j = i + 1; j < this.kartList.length; j++) {
        const kB = this.kartList[j];
        const dx = kA.x - kB.x;
        const dz = kA.z - kB.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        const minDist = 2 * KART_RADIUS;

        if (dist < minDist && dist > 0.001) {
          // Star knockback (B7): if one kart has star, the other gets stunned
          if (kA.starTimer > 0 && kB.starTimer <= 0) {
            this.hitKart(kB, kA.id);
            continue;
          }
          if (kB.starTimer > 0 && kA.starTimer <= 0) {
            this.hitKart(kA, kB.id);
            continue;
          }

          // Normal push apart
          const overlap = minDist - dist;
          const nx = dx / dist;
          const nz = dz / dist;
          kA.x += nx * overlap * 0.5;
          kA.z += nz * overlap * 0.5;
          kB.x -= nx * overlap * 0.5;
          kB.z -= nz * overlap * 0.5;

          // Exchange velocity component along collision normal
          const relV = (kA.vx - kB.vx) * nx + (kA.vz - kB.vz) * nz;
          kA.vx -= relV * nx * BOUNCE;
          kA.vz -= relV * nz * BOUNCE;
          kB.vx += relV * nx * BOUNCE;
          kB.vz += relV * nz * BOUNCE;

          // Recompute speeds from velocity
          kA.speed = Math.sqrt(kA.vx * kA.vx + kA.vz * kA.vz);
          kB.speed = Math.sqrt(kB.vx * kB.vx + kB.vz * kB.vz);

          // Preserve direction of motion in speed sign
          const dotA = kA.vx * Math.cos(kA.yaw) + kA.vz * Math.sin(kA.yaw);
          if (dotA < 0) kA.speed = -kA.speed;
          const dotB = kB.vx * Math.cos(kB.yaw) + kB.vz * Math.sin(kB.yaw);
          if (dotB < 0) kB.speed = -kB.speed;
        }
      }
    }
  }

  // --------------------------------------------------------
  // Items
  // --------------------------------------------------------

  private handleItemBoxCollection(): void {
    for (const kart of this.kartList) {
      // Can pick up if no item AND no roulette spinning AND no held item behind AND no orbit items
      if (kart.item !== null || kart.rouletteTimer > 0 || kart.rouletteResult !== null) continue;
      if (kart.heldItemBehind !== null || kart.orbitItems > 0) continue;
      if (kart.finished || kart.stunTimer > 0) continue;

      for (const box of this.itemBoxes) {
        if (!box.active) continue;

        const dx = kart.x - box.x;
        const dz = kart.z - box.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < ITEM_BOX_RADIUS + KART_RADIUS) {
          // Position-based item selection (B1)
          const itemType = pickItemByPosition(kart.position, this.kartList.length);

          // B10: Start roulette timer; actual item decided now server-side
          kart.rouletteResult = itemType;
          kart.rouletteTimer = config.specialItems.rouletteTime;

          box.active = false;
          box.respawnTimer = ITEM_RESPAWN;
          this.events.onItemPickup(kart.id, itemType, box.index);
          break; // one box per tick per kart
        }
      }
    }

    // Finalize roulette: when timer expires, make item available
    for (const kart of this.kartList) {
      if (kart.rouletteResult !== null && kart.rouletteTimer <= 0) {
        kart.item = kart.rouletteResult;
        kart.rouletteResult = null;

        // B5: Triple variants set orbit items
        if (
          kart.item === ItemType.TripleGreenShell ||
          kart.item === ItemType.TripleRedShell ||
          kart.item === ItemType.TripleBanana
        ) {
          kart.orbitItems = 3;
        }
      }
    }
  }

  private handleItemLogic(kart: ServerKart): void {
    const input = kart.lastInput;

    // B11: Rear item holding — holdable items
    if (kart.item !== null && isHoldableItem(kart.item)) {
      if (input.item && !kart.prevItemHeld) {
        // First press: hold item behind
        if (kart.heldItemBehind === null) {
          kart.heldItemBehind = kart.item;
          kart.item = null;
          return;
        }
      }
    }

    // B11: Release held item behind (button released after holding)
    if (kart.heldItemBehind !== null) {
      if (!input.item && kart.prevItemHeld) {
        // Released the button: deploy backward
        this.deployItemBackward(kart, kart.heldItemBehind);
        kart.heldItemBehind = null;
        return;
      }
      // Still holding: do nothing, shield active
      return;
    }

    // B5: Triple variants: each item press launches one
    if (kart.orbitItems > 0 && kart.item !== null) {
      if (input.item && !kart.prevItemHeld) {
        this.launchOrbitItem(kart);
        return;
      }
      return;
    }

    // Normal item use
    if (input.item && !kart.prevItemHeld && kart.item !== null) {
      this.useItem(kart);
    }
  }

  private deployItemBackward(kart: ServerKart, item: ItemType): void {
    const behindX = kart.x - Math.cos(kart.yaw) * 2;
    const behindZ = kart.z - Math.sin(kart.yaw) * 2;

    switch (item) {
      case ItemType.Banana:
        this.spawnBanana(kart.id, behindX, behindZ);
        break;
      case ItemType.GreenShell: {
        // Fire green shell backward
        const projId = `proj_${this.nextProjectileId++}`;
        this.projectiles.push({
          id: projId,
          ownerId: kart.id,
          x: behindX,
          y: 0,
          z: behindZ,
          vx: -Math.cos(kart.yaw) * config.shells.greenSpeed,
          vz: -Math.sin(kart.yaw) * config.shells.greenSpeed,
          shellType: 'green',
          bounceCount: 0,
          spawnX: kart.x,
          spawnZ: kart.z,
          targetId: null,
          bluePhase: 'fly',
          blueTargetX: 0,
          blueTargetZ: 0,
        });
        break;
      }
    }
  }

  private launchOrbitItem(kart: ServerKart): void {
    if (kart.orbitItems <= 0 || kart.item === null) return;

    kart.orbitItems--;

    switch (kart.item) {
      case ItemType.TripleGreenShell:
        this.fireGreenShell(kart);
        break;
      case ItemType.TripleRedShell:
        this.fireRedShell(kart);
        break;
      case ItemType.TripleBanana: {
        const behindX = kart.x - Math.cos(kart.yaw) * 2;
        const behindZ = kart.z - Math.sin(kart.yaw) * 2;
        this.spawnBanana(kart.id, behindX, behindZ);
        break;
      }
    }

    if (kart.orbitItems <= 0) {
      kart.item = null;
    }
  }

  private useItem(kart: ServerKart): void {
    const item = kart.item;
    kart.item = null;

    switch (item) {
      case ItemType.SpeedBoost:
        kart.boostTimer = BOOST_DURATION;
        if (kart.speed < MAX_SPEED) kart.speed = MAX_SPEED;
        break;

      case ItemType.Projectile: {
        // Legacy projectile (backwards compat)
        const projId = `proj_${this.nextProjectileId++}`;
        this.projectiles.push({
          id: projId,
          ownerId: kart.id,
          x: kart.x + Math.cos(kart.yaw) * (KART_RADIUS + 1),
          y: 0,
          z: kart.z + Math.sin(kart.yaw) * (KART_RADIUS + 1),
          vx: Math.cos(kart.yaw) * config.items.projectileSpeed,
          vz: Math.sin(kart.yaw) * config.items.projectileSpeed,
          shellType: 'legacy',
          bounceCount: 0,
          spawnX: kart.x,
          spawnZ: kart.z,
          targetId: null,
          bluePhase: 'fly',
          blueTargetX: 0,
          blueTargetZ: 0,
        });
        break;
      }

      case ItemType.GreenShell:
        this.fireGreenShell(kart);
        break;

      case ItemType.RedShell:
        this.fireRedShell(kart);
        break;

      case ItemType.Banana: {
        const behindX = kart.x - Math.cos(kart.yaw) * 2;
        const behindZ = kart.z - Math.sin(kart.yaw) * 2;
        this.spawnBanana(kart.id, behindX, behindZ);
        break;
      }

      case ItemType.TripleGreenShell:
      case ItemType.TripleRedShell:
      case ItemType.TripleBanana:
        // These are handled via orbit system; re-assign with orbit
        kart.item = item;
        kart.orbitItems = 3;
        break;

      case ItemType.Lightning:
        this.useLightning(kart);
        break;

      case ItemType.Star:
        this.useStar(kart);
        break;

      case ItemType.BlueShell:
        this.fireBlueShell(kart);
        break;

      case ItemType.Blooper:
        this.useBlooper(kart);
        break;
    }
  }

  // ── Shell firing helpers ──

  private fireGreenShell(kart: ServerKart): void {
    const projId = `proj_${this.nextProjectileId++}`;
    this.projectiles.push({
      id: projId,
      ownerId: kart.id,
      x: kart.x + Math.cos(kart.yaw) * (KART_RADIUS + 1),
      y: 0,
      z: kart.z + Math.sin(kart.yaw) * (KART_RADIUS + 1),
      vx: Math.cos(kart.yaw) * config.shells.greenSpeed,
      vz: Math.sin(kart.yaw) * config.shells.greenSpeed,
      shellType: 'green',
      bounceCount: 0,
      spawnX: kart.x,
      spawnZ: kart.z,
      targetId: null,
      bluePhase: 'fly',
      blueTargetX: 0,
      blueTargetZ: 0,
    });
  }

  private fireRedShell(kart: ServerKart): void {
    // Find nearest kart ahead by race position
    let target: ServerKart | null = null;
    let bestPos = 0;
    for (const other of this.kartList) {
      if (other.id === kart.id || other.finished) continue;
      if (other.position < kart.position) {
        if (target === null || other.position > bestPos) {
          target = other;
          bestPos = other.position;
        }
      }
    }

    // If no one ahead, target nearest behind
    if (!target) {
      for (const other of this.kartList) {
        if (other.id === kart.id || other.finished) continue;
        if (target === null || other.position < bestPos) {
          target = other;
          bestPos = other.position;
        }
      }
    }

    const projId = `proj_${this.nextProjectileId++}`;
    const dirX = Math.cos(kart.yaw);
    const dirZ = Math.sin(kart.yaw);
    this.projectiles.push({
      id: projId,
      ownerId: kart.id,
      x: kart.x + dirX * (KART_RADIUS + 1),
      y: 0,
      z: kart.z + dirZ * (KART_RADIUS + 1),
      vx: dirX * config.shells.redSpeed,
      vz: dirZ * config.shells.redSpeed,
      shellType: 'red',
      bounceCount: 0,
      spawnX: kart.x,
      spawnZ: kart.z,
      targetId: target ? target.id : null,
      bluePhase: 'fly',
      blueTargetX: 0,
      blueTargetZ: 0,
    });
  }

  private fireBlueShell(kart: ServerKart): void {
    // Target position 1 kart
    let leader: ServerKart | null = null;
    for (const other of this.kartList) {
      if (other.finished) continue;
      if (other.position === 1) {
        leader = other;
        break;
      }
    }

    if (!leader) return; // no valid target

    const projId = `proj_${this.nextProjectileId++}`;
    const dirX = Math.cos(kart.yaw);
    const dirZ = Math.sin(kart.yaw);
    this.projectiles.push({
      id: projId,
      ownerId: kart.id,
      x: kart.x + dirX * (KART_RADIUS + 1),
      y: config.shells.blueShellFlyHeight,
      z: kart.z + dirZ * (KART_RADIUS + 1),
      vx: dirX * config.shells.blueShellSpeed,
      vz: dirZ * config.shells.blueShellSpeed,
      shellType: 'blue',
      bounceCount: 0,
      spawnX: kart.x,
      spawnZ: kart.z,
      targetId: leader.id,
      bluePhase: 'fly',
      blueTargetX: leader.x,
      blueTargetZ: leader.z,
    });
  }

  // ── Special items ──

  private useLightning(kart: ServerKart): void {
    // B6: All opponents get shrunk, lose coins, drop items
    for (const other of this.kartList) {
      if (other.id === kart.id || other.finished) continue;
      if (other.starTimer > 0) continue; // star immunity

      other.shrinkTimer = config.specialItems.lightningDuration;
      // Lose coins
      other.coins = Math.max(0, other.coins - config.coins.lostOnHit);
      // Drop items
      if (other.item !== null) {
        other.item = null;
      }
      if (other.heldItemBehind !== null) {
        other.heldItemBehind = null;
      }
      other.orbitItems = 0;
      other.rouletteResult = null;
      other.rouletteTimer = 0;
      // Brief stun
      other.stunTimer = Math.max(other.stunTimer, 0.5);
    }
    this.events.onLightningStrike?.(kart.id);
  }

  private useStar(kart: ServerKart): void {
    // B7: Invincibility + speed boost
    kart.starTimer = config.specialItems.starDuration;
  }

  private useBlooper(kart: ServerKart): void {
    // B9: All opponents get ink on screen
    for (const other of this.kartList) {
      if (other.id === kart.id || other.finished) continue;
      if (other.starTimer > 0) continue; // star immunity
      other.blooperTimer = config.specialItems.blooperDuration;
    }
    this.events.onBlooperInk?.(kart.id);
  }

  // ── Banana helpers ──

  private spawnBanana(ownerId: string, x: number, z: number): void {
    const id = `banana_${this.nextBananaId++}`;
    this.bananas.push({
      id,
      ownerId,
      x,
      z,
      spawnTime: this.currentTick,
    });
  }

  // --------------------------------------------------------
  // Projectiles
  // --------------------------------------------------------

  private updateProjectiles(dt: number): void {
    const toRemove: Set<string> = new Set();

    for (const proj of this.projectiles) {
      if (proj.shellType === 'blue') {
        this.updateBlueShell(proj, dt, toRemove);
        continue;
      }

      if (proj.shellType === 'red') {
        this.updateRedShell(proj, dt, toRemove);
        continue;
      }

      // Green shell / legacy: move linearly
      proj.x += proj.vx * dt;
      proj.z += proj.vz * dt;

      // Check wall collision
      const wall = checkWallCollision(this.track.path, proj.x, proj.z, this.roadWidth / 2, PROJECTILE_RADIUS);
      if (wall.hit) {
        if (proj.shellType === 'green') {
          // B2: Green shells bounce off walls
          proj.bounceCount++;
          if (proj.bounceCount > config.shells.greenBounces) {
            toRemove.add(proj.id);
            continue;
          }
          // Reflect velocity along wall normal
          const dot = proj.vx * wall.normalX + proj.vz * wall.normalZ;
          proj.vx -= 2 * dot * wall.normalX;
          proj.vz -= 2 * dot * wall.normalZ;
          // Push out of wall
          proj.x = wall.pushX;
          proj.z = wall.pushZ;
        } else {
          // Legacy projectile: destroy on wall hit
          toRemove.add(proj.id);
          continue;
        }
      }

      // Check distance from spawn -- remove if traveled too far
      const dxSpawn = proj.x - proj.spawnX;
      const dzSpawn = proj.z - proj.spawnZ;
      if (dxSpawn * dxSpawn + dzSpawn * dzSpawn > 200 * 200) {
        toRemove.add(proj.id);
        continue;
      }

      // Check kart collision
      this.checkProjectileKartHit(proj, toRemove);
    }

    // Remove marked projectiles
    if (toRemove.size > 0) {
      this.projectiles = this.projectiles.filter((p) => !toRemove.has(p.id));
    }
  }

  private updateRedShell(proj: ServerProjectile, dt: number, toRemove: Set<string>): void {
    // B3: Home toward target kart
    if (proj.targetId) {
      const target = this.karts.get(proj.targetId);
      if (target && !target.finished) {
        const dx = target.x - proj.x;
        const dz = target.z - proj.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist > 0.1) {
          const desiredAngle = Math.atan2(dz, dx);
          const currentAngle = Math.atan2(proj.vz, proj.vx);
          let angleDiff = desiredAngle - currentAngle;
          while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
          while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

          const maxTurn = config.shells.redTurnRate * dt;
          const turn = clamp(angleDiff, -maxTurn, maxTurn);
          const newAngle = currentAngle + turn;
          proj.vx = Math.cos(newAngle) * config.shells.redSpeed;
          proj.vz = Math.sin(newAngle) * config.shells.redSpeed;
        }
      }
    }

    proj.x += proj.vx * dt;
    proj.z += proj.vz * dt;

    // Wall collision: red shells are destroyed on wall hit
    const wall = checkWallCollision(this.track.path, proj.x, proj.z, this.roadWidth / 2, PROJECTILE_RADIUS);
    if (wall.hit) {
      toRemove.add(proj.id);
      return;
    }

    // Distance limit
    const dxSpawn = proj.x - proj.spawnX;
    const dzSpawn = proj.z - proj.spawnZ;
    if (dxSpawn * dxSpawn + dzSpawn * dzSpawn > 300 * 300) {
      toRemove.add(proj.id);
      return;
    }

    this.checkProjectileKartHit(proj, toRemove);
  }

  private updateBlueShell(proj: ServerProjectile, dt: number, toRemove: Set<string>): void {
    // B8: Blue shell targets position 1
    // Find current leader
    let leader: ServerKart | null = null;
    for (const k of this.kartList) {
      if (k.finished) continue;
      if (k.position === 1) {
        leader = k;
        break;
      }
    }

    if (!leader) {
      toRemove.add(proj.id);
      return;
    }

    // Update target position
    proj.blueTargetX = leader.x;
    proj.blueTargetZ = leader.z;

    const dx = proj.blueTargetX - proj.x;
    const dz = proj.blueTargetZ - proj.z;
    const horizDist = Math.sqrt(dx * dx + dz * dz);

    if (proj.bluePhase === 'fly') {
      // Fly at high altitude toward target
      proj.y = config.shells.blueShellFlyHeight;

      // Steer toward target
      if (horizDist > 0.1) {
        const desiredAngle = Math.atan2(dz, dx);
        const currentAngle = Math.atan2(proj.vz, proj.vx);
        let angleDiff = desiredAngle - currentAngle;
        while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
        while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

        const maxTurn = 5.0 * dt; // fast homing
        const turn = clamp(angleDiff, -maxTurn, maxTurn);
        const newAngle = currentAngle + turn;
        proj.vx = Math.cos(newAngle) * config.shells.blueShellSpeed;
        proj.vz = Math.sin(newAngle) * config.shells.blueShellSpeed;
      }

      proj.x += proj.vx * dt;
      proj.z += proj.vz * dt;

      // Start diving when close
      if (horizDist < 10) {
        proj.bluePhase = 'dive';
      }
    } else {
      // Dive phase: descend toward target
      proj.x += proj.vx * dt * 0.3;
      proj.z += proj.vz * dt * 0.3;
      proj.y -= 40 * dt; // fast descent

      if (proj.y <= 0) {
        proj.y = 0;
        // Blast radius (B8)
        const blastR = config.shells.blueShellBlastRadius;
        for (const k of this.kartList) {
          if (k.finished) continue;
          const kdx = k.x - proj.x;
          const kdz = k.z - proj.z;
          const kDist = Math.sqrt(kdx * kdx + kdz * kdz);
          if (kDist < blastR) {
            this.hitKart(k, proj.ownerId);
          }
        }
        toRemove.add(proj.id);
      }
    }
  }

  private checkProjectileKartHit(proj: ServerProjectile, toRemove: Set<string>): void {
    for (const kart of this.kartList) {
      if (kart.id === proj.ownerId) continue; // don't hit owner
      if (kart.finished || kart.stunTimer > 0) continue;

      const dx = proj.x - kart.x;
      const dz = proj.z - kart.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < PROJECTILE_RADIUS + KART_RADIUS) {
        // B7: Star immunity
        if (kart.starTimer > 0) {
          toRemove.add(proj.id);
          break;
        }

        // B11: Check held-item-behind absorb (rear hit)
        if (kart.heldItemBehind !== null) {
          // Check if projectile is coming from behind
          const kartFwdX = Math.cos(kart.yaw);
          const kartFwdZ = Math.sin(kart.yaw);
          const projDir = dx * kartFwdX + dz * kartFwdZ;
          if (projDir < 0) {
            // Projectile is behind the kart: absorbed by held item
            kart.heldItemBehind = null;
            toRemove.add(proj.id);
            break;
          }
        }

        // Hit!
        this.hitKart(kart, proj.ownerId);
        toRemove.add(proj.id);
        break;
      }
    }
  }

  // --------------------------------------------------------
  // Orbit shield (B5)
  // --------------------------------------------------------

  private handleOrbitShield(): void {
    const toRemove: Set<string> = new Set();

    for (const proj of this.projectiles) {
      if (toRemove.has(proj.id)) continue;

      for (const kart of this.kartList) {
        if (kart.orbitItems <= 0) continue;
        if (kart.id === proj.ownerId) continue;

        const dx = proj.x - kart.x;
        const dz = proj.z - kart.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < 2.5) {
          // Orbit item absorbs the projectile
          kart.orbitItems--;
          if (kart.orbitItems <= 0 && kart.item !== null) {
            // Check if this was a triple item
            if (
              kart.item === ItemType.TripleGreenShell ||
              kart.item === ItemType.TripleRedShell ||
              kart.item === ItemType.TripleBanana
            ) {
              kart.item = null;
            }
          }
          toRemove.add(proj.id);
          break;
        }
      }
    }

    if (toRemove.size > 0) {
      this.projectiles = this.projectiles.filter((p) => !toRemove.has(p.id));
    }
  }

  // --------------------------------------------------------
  // Banana collisions (B4)
  // --------------------------------------------------------

  private handleBananaCollisions(): void {
    const toRemove: Set<string> = new Set();

    for (const banana of this.bananas) {
      if (toRemove.has(banana.id)) continue;

      for (const kart of this.kartList) {
        if (kart.finished || kart.stunTimer > 0) continue;

        // Owner immunity for 0.5 seconds (~15 ticks at 30 tps)
        if (kart.id === banana.ownerId && (this.currentTick - banana.spawnTime) < 15) {
          continue;
        }

        const dx = kart.x - banana.x;
        const dz = kart.z - banana.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < KART_RADIUS + 1.0) {
          // B7: Star immunity
          if (kart.starTimer > 0) {
            toRemove.add(banana.id);
            break;
          }

          // Hit by banana: stun + spin
          this.hitKart(kart, banana.ownerId);
          toRemove.add(banana.id);
          break;
        }
      }
    }

    if (toRemove.size > 0) {
      this.bananas = this.bananas.filter((b) => !toRemove.has(b.id));
    }
  }

  // --------------------------------------------------------
  // Hit a kart (centralized stun + coin loss)
  // --------------------------------------------------------

  private hitKart(kart: ServerKart, byId: string): void {
    if (kart.starTimer > 0) return; // star immunity
    kart.stunTimer = STUN_DURATION;
    kart.coins = Math.max(0, kart.coins - config.coins.lostOnHit);
    // Drop held item
    if (kart.item !== null) {
      kart.item = null;
    }
    if (kart.heldItemBehind !== null) {
      kart.heldItemBehind = null;
    }
    kart.orbitItems = 0;
    kart.rouletteResult = null;
    kart.rouletteTimer = 0;
    this.events.onPlayerHit(kart.id, byId);
  }

  // --------------------------------------------------------
  // Coin collection (A6)
  // --------------------------------------------------------

  private handleCoinCollection(): void {
    const pickupR = config.coins.pickupRadius;
    const maxCoins = config.coins.maxCoins;

    for (const kart of this.kartList) {
      if (kart.finished || kart.stunTimer > 0) continue;
      if (kart.coins >= maxCoins) continue;

      for (const coin of this.coins) {
        if (!coin.active) continue;

        const dx = kart.x - coin.x;
        const dz = kart.z - coin.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < pickupR + KART_RADIUS) {
          kart.coins = Math.min(maxCoins, kart.coins + 1);
          coin.active = false;
          coin.respawnTimer = config.coins.respawnTime;
        }
      }
    }
  }

  // --------------------------------------------------------
  // Hazard collisions
  // --------------------------------------------------------

  private handleHazardCollisions(): void {
    for (const kart of this.kartList) {
      if (kart.finished || kart.stunTimer > 0) continue;
      if (kart.starTimer > 0) continue; // star immunity

      const hazard = checkHazardCollision(this.hazards, kart.x, kart.z, KART_RADIUS);
      if (hazard) {
        kart.stunTimer = STUN_DURATION;
        kart.coins = Math.max(0, kart.coins - config.coins.lostOnHit);
      }
    }
  }

  // --------------------------------------------------------
  // Checkpoints & laps
  // --------------------------------------------------------

  private updateCheckpointsAndLaps(): void {
    for (const kart of this.kartList) {
      if (kart.finished) continue;

      const closest = findClosestPoint(this.track.path, kart.x, kart.z);
      kart.progress = closest.progress;

      const result = updateCheckpoint(
        closest.index,
        this.track.numPoints,
        kart.checkpoint,
        kart.lap,
        kart.checkpointBits
      );

      kart.checkpoint = result.checkpoint;
      kart.checkpointBits = result.bits;

      if (result.lapCompleted) {
        kart.lap = result.lap;
        this.events.onLapComplete(kart.id, kart.lap);

        if (kart.lap >= TOTAL_LAPS) {
          kart.finished = true;
          kart.finishTime = Date.now() - this.raceStartTime;
        }
      }
    }
  }

  // --------------------------------------------------------
  // Race positions
  // --------------------------------------------------------

  private updateRacePositions(): void {
    // Sort karts for position ranking
    const sorted = [...this.kartList].sort((a, b) => {
      // Finished karts always rank higher (earlier finish = better)
      if (a.finished && !b.finished) return -1;
      if (!a.finished && b.finished) return 1;
      if (a.finished && b.finished) return a.finishTime - b.finishTime;

      // By lap descending
      if (a.lap !== b.lap) return b.lap - a.lap;
      // By checkpoint descending
      if (a.checkpoint !== b.checkpoint) return b.checkpoint - a.checkpoint;
      // By progress descending
      return b.progress - a.progress;
    });

    for (let i = 0; i < sorted.length; i++) {
      sorted[i].position = i + 1;
    }
  }

  // --------------------------------------------------------
  // Snapshot
  // --------------------------------------------------------

  private buildSnapshot(): Snapshot {
    const karts: KartState[] = this.kartList.map((k) => ({
      id: k.id,
      name: k.name,
      x: k.x,
      y: k.y,
      z: k.z,
      vx: k.vx,
      vy: k.vy,
      vz: k.vz,
      yaw: k.yaw,
      speed: k.speed,
      steering: k.steering,
      lap: k.lap,
      checkpoint: k.checkpoint,
      progress: k.progress,
      position: k.position,
      item: k.item,
      stunTimer: k.stunTimer,
      boostTimer: k.boostTimer,
      finished: k.finished,
      finishTime: k.finishTime,
      color: k.color,
      driftTime: k.driftTime,
      coins: k.coins,
      slipstreamTimer: k.slipstreamTimer,
      starTimer: k.starTimer,
      shrinkTimer: k.shrinkTimer,
      blooperTimer: k.blooperTimer,
      wrongWay: k.wrongWay,
      offRoad: k.offRoad,
      heldItemBehind: k.heldItemBehind,
      orbitItems: k.orbitItems,
      rouletteTimer: k.rouletteTimer,
      trickBoosting: k.trickBoosting,
    }));

    const items: ItemBoxState[] = this.itemBoxes.map((b) => ({
      index: b.index,
      x: b.x,
      z: b.z,
      active: b.active,
      respawnTimer: b.respawnTimer,
    }));

    const projectiles: ProjectileState[] = this.projectiles.map((p) => ({
      id: p.id,
      ownerId: p.ownerId,
      x: p.x,
      y: p.y,
      z: p.z,
      vx: p.vx,
      vz: p.vz,
      shellType: p.shellType,
      bounceCount: p.bounceCount,
    }));

    const bananas: BananaState[] = this.bananas.map((b) => ({
      id: b.id,
      ownerId: b.ownerId,
      x: b.x,
      z: b.z,
    }));

    const coins: CoinState[] = this.coins.map((c) => ({
      index: c.index,
      x: c.x,
      z: c.z,
      active: c.active,
      respawnTimer: c.respawnTimer,
    }));

    const lastSeq: Record<string, number> = {};
    for (const k of this.kartList) {
      lastSeq[k.id] = k.lastInputSeq;
    }

    return {
      type: 'snapshot',
      tick: this.currentTick,
      time: Date.now(),
      karts,
      items,
      projectiles,
      bananas,
      coins,
      lastSeq,
    };
  }

  private buildRankings(): RaceResult[] {
    const sorted = [...this.kartList].sort((a, b) => {
      if (a.finished && !b.finished) return -1;
      if (!a.finished && b.finished) return 1;
      if (a.finished && b.finished) return a.finishTime - b.finishTime;
      // Unfinished karts ranked by progress
      if (a.lap !== b.lap) return b.lap - a.lap;
      if (a.checkpoint !== b.checkpoint) return b.checkpoint - a.checkpoint;
      return b.progress - a.progress;
    });

    return sorted.map((k, i) => ({
      playerId: k.id,
      playerName: k.name,
      time: k.finishTime,
      position: i + 1,
    }));
  }
}

// ============================================================
// Utility
// ============================================================

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
